import { TSS } from '../../tss/TSS';
import { UINodeConfig } from '../../tss/types';
import { formatDenomination } from './BillDenominations';
import { getSidebetIconUrl, SIDEBET_ICON_SPECS } from '../../ui/components/sidebetIcon';
import * as THREE from 'three';

type OwnerLabelUpdate = {
  name: string;
  betType: string;
  avatarText?: string;
  avatarUrl?: string;
  betAmount?: number;
  locked?: boolean;
};

/**
 * Map of sidebet betType (uppercase, as it arrives in the server's
 * BetZone.betType) to its inline-SVG data URI. Same shared registry
 * as the bottom-bar buttons and the profile-card chips, so the
 * three surfaces stay visually identical.
 *
 * Keys are upper-snake-case for backwards-compat with how betType
 * arrives here; the registry in sidebetIcon.ts uses lower-snake-case
 * (matching server enums), so we map upper → lower at module load.
 */
const SIDEBET_ICON_PATHS: Record<string, string> = Object.keys(SIDEBET_ICON_SPECS).reduce(
  (acc, key) => {
    // Zone label icons render small in the 3D scene — use the small
    // variant for max readable text fill.
    acc[key.toUpperCase()] = getSidebetIconUrl(key, 'small');
    return acc;
  },
  {} as Record<string, string>,
);

export class DropZoneOwnerLabel {
  // Default avatar image (fallback for banker/create zones with no picture)
  private static avatarImage: HTMLImageElement | null = null;
  private static avatarReady = false;
  private static avatarListeners = new Set<() => void>();

  // Per-player avatar images keyed by URL (shared across all labels).
  private static playerAvatars = new Map<string, HTMLImageElement>();
  private static playerAvatarsReady = new Set<string>();

  // Sidebet icon images (shared across all labels)
  private static sidebetIcons = new Map<string, HTMLImageElement>();
  private static sidebetIconsReady = new Set<string>();
  private static sidebetIconListeners = new Set<() => void>();

  // Persistent redraw subscribers — fired whenever any async image (default
  // avatar, a player avatar, or a sidebet icon) finishes loading so already
  // built labels repaint with the now-ready bitmap. Unlike `avatarListeners`
  // (one-shot, cleared after the default loads), this survives the whole
  // label lifetime and is removed in dispose().
  private static redrawListeners = new Set<() => void>();

  private component: TSS;
  private lastName = '';
  private lastBetType = '';
  private lastAvatarText = '';
  private lastAvatarUrl = '';
  private lastBetAmount = 0;
  private lastLocked = false;
  private redrawListener = () => {
    this.update({ name: this.lastName, betType: this.lastBetType, avatarText: this.lastAvatarText, avatarUrl: this.lastAvatarUrl, betAmount: this.lastBetAmount, locked: this.lastLocked }, true);
  };

  constructor(parent: THREE.Object3D, data: OwnerLabelUpdate) {
    DropZoneOwnerLabel.loadAvatarImage();
    DropZoneOwnerLabel.loadSidebetIcons();
    if (data.avatarUrl) DropZoneOwnerLabel.loadPlayerAvatar(data.avatarUrl);

    this.component = new TSS(
      this.buildTree(data),
      {
        // Bumped from 0.7 → 0.9 so the owner card reads a touch larger in-world.
        worldHeight: 0.9,
        mode: 'sprite',
        resolution: 1,
        depthTest: true,
        depthWrite: false,
        spriteCenter: { x: 0.68, y: 0.5 },
      }
    );

    parent.add(this.component.object3D);
    this.lastName = data.name;
    this.lastBetType = data.betType;
    this.lastAvatarText = data.avatarText ?? '';
    this.lastAvatarUrl = data.avatarUrl ?? '';
    this.lastBetAmount = data.betAmount ?? 0;
    this.lastLocked = data.locked ?? false;

    if (!DropZoneOwnerLabel.avatarReady) {
      DropZoneOwnerLabel.avatarListeners.add(this.redrawListener);
    }
    DropZoneOwnerLabel.sidebetIconListeners.add(this.redrawListener);
    DropZoneOwnerLabel.redrawListeners.add(this.redrawListener);
  }

  /**
   * Load a player's avatar picture (remote CDN URL or local asset). Drawn
   * onto the TSS canvas, so it MUST be cross-origin enabled or the canvas
   * taints and the WebGL texture upload fails. On error we leave it unready
   * → the label falls back to the default dice avatar / initial.
   */
  private static loadPlayerAvatar(url: string): void {
    if (DropZoneOwnerLabel.playerAvatars.has(url)) return;
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => {
      DropZoneOwnerLabel.playerAvatarsReady.add(url);
      for (const listener of DropZoneOwnerLabel.redrawListeners) listener();
    };
    image.onerror = () => {
      // Allow a later retry and keep the fallback avatar in the meantime.
      DropZoneOwnerLabel.playerAvatars.delete(url);
    };
    image.src = url;
    DropZoneOwnerLabel.playerAvatars.set(url, image);
  }

  private static getPlayerAvatar(url: string | undefined): HTMLImageElement | null {
    if (!url) return null;
    if (!DropZoneOwnerLabel.playerAvatarsReady.has(url)) return null;
    return DropZoneOwnerLabel.playerAvatars.get(url) ?? null;
  }

  private static loadAvatarImage(): void {
    if (DropZoneOwnerLabel.avatarImage) return;
    const image = new Image();
    image.onload = () => {
      DropZoneOwnerLabel.avatarReady = true;
      for (const listener of DropZoneOwnerLabel.avatarListeners) {
        listener();
      }
      DropZoneOwnerLabel.avatarListeners.clear();
    };
    image.src = '/assets/avatars/default/fire-dice.svg';
    DropZoneOwnerLabel.avatarImage = image;
  }

  private static loadSidebetIcons(): void {
    for (const [key, path] of Object.entries(SIDEBET_ICON_PATHS)) {
      if (DropZoneOwnerLabel.sidebetIcons.has(key)) continue;
      const img = new Image();
      img.onload = () => {
        DropZoneOwnerLabel.sidebetIconsReady.add(key);
        // Redraw all labels when any icon loads
        if (DropZoneOwnerLabel.sidebetIconsReady.size === Object.keys(SIDEBET_ICON_PATHS).length) {
          for (const listener of DropZoneOwnerLabel.sidebetIconListeners) {
            listener();
          }
        }
      };
      img.src = path;
      DropZoneOwnerLabel.sidebetIcons.set(key, img);
    }
  }

  /** Check if this betType has a sidebet icon */
  private static getSidebetIcon(betType: string): HTMLImageElement | null {
    const key = betType.toUpperCase();
    if (!SIDEBET_ICON_PATHS[key]) return null;
    if (!DropZoneOwnerLabel.sidebetIconsReady.has(key)) return null;
    return DropZoneOwnerLabel.sidebetIcons.get(key) ?? null;
  }

  private buildTree(data: OwnerLabelUpdate): UINodeConfig {
    const avatarText = data.avatarText ?? '';
    const betAmount = data.betAmount ?? 0;
    const locked = data.locked ?? false;
    const fallbackInitial = data.name.trim().charAt(0).toUpperCase() || '?';
    const initial = avatarText.trim().charAt(0) || fallbackInitial;

    // Right side: amount or lock icon
    let rightChild: UINodeConfig;
    if (locked) {
      rightChild = {
        tag: 'amount',
        text: '\u{1F512}',
        style: { fontWeight: 'bold', fontSize: 36, fontFamily: 'Oswald, sans-serif', color: '#FFD700' },
      };
    } else if (betAmount > 0) {
      rightChild = {
        tag: 'amount',
        text: formatDenomination(betAmount),
        style: { fontWeight: 'normal', fontSize: 50, fontFamily: 'Oswald, sans-serif', color: '#7dc243', margin: [0, 0, 0, 20] },
      };
    } else {
      rightChild = {
        tag: 'amount',
        text: '',
        style: { fontSize: 1 },
      };
    }

    // Check if this is a sidebet with an icon
    const sidebetIcon = DropZoneOwnerLabel.getSidebetIcon(data.betType);

    // Left side: hex icon for sidebets, round avatar for banker/create
    let leftChild: UINodeConfig;
    if (sidebetIcon) {
      // Hex icon — rendered as a square image node (no clipping)
      leftChild = {
        tag: 'icon',
        image: sidebetIcon,
        style: { width: 50, height: 50 },
      };
    } else {
      // Round avatar for banker/create zones. Prefer the owner's actual
      // profile picture; fall back to the default dice avatar, then to the
      // name initial while either is still loading.
      const playerAvatar = DropZoneOwnerLabel.getPlayerAvatar(data.avatarUrl);
      const avatarImg = playerAvatar
        ?? (DropZoneOwnerLabel.avatarReady ? DropZoneOwnerLabel.avatarImage ?? undefined : undefined);
      leftChild = {
        tag: 'avatar',
        style: { color: '#000000', fontFamily: 'Oswald, sans-serif' },
        circle: {
          radius: 25,
          fill: '#FFFFFF',
          image: avatarImg,
          text: avatarImg ? undefined : initial,
          textSize: 30,
        },
      };
    }

    // Info column: for sidebets just show name (icon already conveys bet type)
    // For banker/create zones show name + betType
    const infoChildren: UINodeConfig[] = [
      {
        tag: 'name',
        text: data.name,
        style: {
          fontWeight: 'bold',
          fontSize: 18,
          fontFamily: 'Oswald, sans-serif',
          color: '#FFFFFF',
          maxWidth: 140,
          overflow: 'ellipsis',
        },
      },
    ];

    if (!sidebetIcon) {
      // Non-sidebet: show betType text
      infoChildren.push({
        tag: 'betType',
        text: data.betType,
        style: {
          fontSize: 14,
          fontFamily: 'Oswald, sans-serif',
          color: '#FFFFFF',
        },
      });
    }

    return {
      tag: 'root',
      style: {
        direction: 'column',
        alignItems: 'center',
        // Tiny negative-style overlap: the triangle sits flush against the
        // pill's bottom edge. No gap — the triangle's top edge IS the pill's
        // bottom edge, producing a single fused speech-bubble shape.
        gap: 0,
      },
      children: [
        {
          tag: 'pill',
          style: {
            direction: 'row',
            alignItems: 'center',
            padding: [10, 12, 10, 12],
            gap: 6,
            backgroundColor: '#000000',
            borderRadius: 12,
          },
          children: [
            leftChild,
            {
              tag: 'info',
              style: { direction: 'column', gap: 2 },
              children: infoChildren,
            },
            rightChild,
          ],
        },
        {
          // Speech-bubble pointer below the pill, matching the pill's black.
          // Sized to read at the worldHeight: 0.7 of the parent component.
          tag: 'arrow',
          triangle: { width: 44, height: 14, direction: 'down', fill: '#000000' },
        },
      ],
    };
  }

  update(data: OwnerLabelUpdate, force = false): void {
    const avatarText = data.avatarText ?? '';
    const avatarUrl = data.avatarUrl ?? '';
    const betAmount = data.betAmount ?? 0;
    const locked = data.locked ?? false;
    if (avatarUrl) DropZoneOwnerLabel.loadPlayerAvatar(avatarUrl);
    if (!force &&
      data.name === this.lastName &&
      data.betType === this.lastBetType &&
      avatarText === this.lastAvatarText &&
      avatarUrl === this.lastAvatarUrl &&
      betAmount === this.lastBetAmount &&
      locked === this.lastLocked
    ) {
      return;
    }

    this.lastName = data.name;
    this.lastBetType = data.betType;
    this.lastAvatarText = avatarText;
    this.lastAvatarUrl = avatarUrl;
    this.lastBetAmount = betAmount;
    this.lastLocked = locked;

    this.component.setTree(this.buildTree(data));
    this.component.render();
  }

  setPosition(x: number, y: number, z: number): void {
    this.component.setPosition(x, y, z);
  }

  setVisible(visible: boolean): void {
    this.component.setVisible(visible);
  }

  reset(): void {
    this.component.reset();
    this.lastName = '';
    this.lastBetType = '';
    this.lastAvatarText = '';
    this.lastAvatarUrl = '';
    this.lastBetAmount = 0;
    this.lastLocked = false;
  }

  attachTo(parent: THREE.Object3D): void {
    this.component.attachTo(parent);
  }

  dispose(): void {
    DropZoneOwnerLabel.avatarListeners.delete(this.redrawListener);
    DropZoneOwnerLabel.sidebetIconListeners.delete(this.redrawListener);
    DropZoneOwnerLabel.redrawListeners.delete(this.redrawListener);
    this.component.dispose();
  }
}
